import { useState } from "react";
import bitsByWordsCount from "../static/bitsByWordCount.json";
import { ethers } from "ethers";
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Box,
  IconButton,
  Typography,
  TextField, // Import TextField
} from "@mui/material";
import { ArrowBackIosNew, ArrowForwardIos } from "@mui/icons-material";

const WALLETS_PER_PAGE = 10;

export default function DerivedWallets({
  diceNumbers,
  passPhrase,
  wordsCount,
  network,
}) {
  const [page, setPage] = useState(0);

  function getBitString() {
    return diceNumbers
      .map((e) => Number(e).toString(2))
      .join("")
      .slice(0, bitsByWordsCount[wordsCount]);
  }
  function finalBitsToWords() {
    const finalBits = getBitString();

    if (bitsByWordsCount[wordsCount] > finalBits.length) {
      return {
        success: false,
        mnemonic: "Cannot generate wallets. Not enough entropy.",
      };
    }

    // bits to bytes
    const bytes = [];
    for (let i = 0; i < finalBits.length; i += 8) {
      const byte = finalBits.slice(i, i + 8);
      bytes.push(parseInt(byte, 2));
    }

    const mnemonic = ethers.Mnemonic.fromEntropy(
      new Uint8Array(bytes),
      passPhrase
    );
    return { success: true, mnemonic };
  }

  const { success, mnemonic } = finalBitsToWords();

  const handleNextPage = () => {
    setPage((prevPage) => prevPage + 1);
  };

  const handlePrevPage = () => {
    setPage((prevPage) => Math.max(0, prevPage - 1));
  };

  const handlePageInputChange = (event) => {
    const value = event.target.value;
    if (value === "") {
      // Optionally handle empty input, e.g., show a blank or current page
      return;
    }

    const newPage = parseInt(value, 10);
    if (!isNaN(newPage) && newPage > 0) {
      setPage(newPage - 1); // Convert back to 0-indexed page
    }
    // Optionally, provide feedback for invalid input
  };

  return (
    <Card>
      <CardHeader title="Derived Wallets" />
      <CardContent>
        {success ? (
          <>
            <TableContainer component={Paper}>
              <Table stickyHeader aria-label="derived wallets table">
                <TableHead>
                  <TableRow>
                    <TableCell>Path</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Public Key</TableCell>
                    <TableCell>Private Key</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array(WALLETS_PER_PAGE)
                    .fill(0)
                    .map((_, i) => {
                      const index = page * WALLETS_PER_PAGE + i;
                      const path = `m/44'/${network}'/0'/0/${index}`;
                      const wallet = ethers.HDNodeWallet.fromMnemonic(
                        mnemonic,
                        path
                      );

                      return (
                        <TableRow
                          key={index}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell component="th" scope="row">
                            {path}
                          </TableCell>
                          <TableCell>{wallet.address}</TableCell>
                          <TableCell sx={{ wordBreak: "break-all" }}>
                            {wallet.publicKey}
                          </TableCell>
                          <TableCell sx={{ wordBreak: "break-all" }}>
                            {wallet.privateKey}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end", // Align to right
                alignItems: "center",
                pt: 2,
              }}
            >
              <IconButton onClick={handlePrevPage} disabled={page === 0}>
                <ArrowBackIosNew />
              </IconButton>
              <TextField
                variant="outlined"
                size="small"
                value={page + 1} // Display 1-indexed page number
                onChange={handlePageInputChange}
                sx={{
                  width: "60px",
                  mx: 1,
                  "& .MuiOutlinedInput-input": { textAlign: "center" },
                }}
              />
              <IconButton onClick={handleNextPage}>
                <ArrowForwardIos />
              </IconButton>
            </Box>
          </>
        ) : (
          <Alert severity="warning">{mnemonic}</Alert>
        )}
      </CardContent>
    </Card>
  );
}
